import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FiX } from 'react-icons/fi';
import { requestPersonPhoneVerification, verifyPersonPhoneCode } from '../../services/api';
import styles from './PersonPhoneVerificationModal.module.css';

const COOLDOWN_SECONDS = 60;

const PersonPhoneVerificationModal = ({ personId, phone, onClose, onVerified }) => {
  const [step, setStep] = useState('request'); // 'request' | 'verify'
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const handleSendCode = async () => {
    setSending(true);
    try {
      await requestPersonPhoneVerification(personId);
      toast.success('Verification code sent on WhatsApp');
      setStep('verify');
      setCooldown(COOLDOWN_SECONDS);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send verification code');
    } finally {
      setSending(false);
    }
  };

  const handleVerify = async () => {
    if (!/^\d{6}$/.test(code)) { toast.error('Enter the 6-digit code'); return; }
    setVerifying(true);
    try {
      await verifyPersonPhoneCode(personId, code);
      toast.success('Phone number verified successfully');
      onVerified();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid or expired code');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div>
            <h3>Verify Phone Number</h3>
            <p className={styles.modalSubtitle}>
              {step === 'request'
                ? `We will send a 6-digit code to ${phone} on WhatsApp.`
                : `Enter the 6-digit code sent to ${phone} on WhatsApp.`}
            </p>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose} type="button"><FiX size={16} /></button>
        </div>

        {step === 'request' && (
          <div className={styles.modalActions}>
            <button className="btn btn-secondary btn-sm" onClick={onClose} type="button">Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={handleSendCode} disabled={sending} type="button">
              {sending ? 'Sending…' : 'Send Code'}
            </button>
          </div>
        )}

        {step === 'verify' && (
          <>
            <div className={styles.field}>
              <label>Verification Code</label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
              />
            </div>
            <div className={styles.resendRow}>
              <span>Didn't get the code?</span>
              <button
                className={styles.resendLink}
                type="button"
                disabled={cooldown > 0 || sending}
                onClick={handleSendCode}
              >
                {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
              </button>
            </div>
            <div className={styles.modalActions}>
              <button className="btn btn-secondary btn-sm" onClick={onClose} type="button">Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={handleVerify} disabled={verifying} type="button">
                {verifying ? 'Verifying…' : 'Verify'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PersonPhoneVerificationModal;
